import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, computed, makeObservable, observable } from 'mobx';

import { Tracking } from '@/application/utils/tracking';
import { ApplicationModel } from '@/presentation/models/application.model';
import { ExercisesModel } from '@/presentation/models/exercises.model';
import { UserModel } from '@/presentation/models/user.model';

import { QuizProps } from './types';

@ViewModel({ singleton: true })
export class QuizzesViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable hasError = false;
  @observable quizzes: QuizProps[] = [];

  constructor(
    private readonly applicationModel: ApplicationModel,
    private readonly userModel: UserModel,
    private readonly exercisesModel: ExercisesModel,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    Tracking.pageView();
    this.getContent();
  }

  @action.bound
  async getContent() {
    try {
      this.isLoading = true;
      this.hasError = false;
      if (!this.exercisesModel.quizzes?.length) {
        await this.exercisesModel.getExercisesByClassifier();
      }
      this.transformQuizzesData();
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @action
  transformQuizzesData() {
    this.quizzes = this.exercisesModel.quizzes.map(this.transformQuizData);
  }

  transformQuizData = (quiz: QuizProps): QuizProps => {
    const isComingSoon = this.isComingSoonQuiz(quiz);
    return {
      ...quiz,
      score: quiz.studentFinished
        ? this.calculateScore(quiz.correctAnswers, quiz.totalQuestions)
        : null,
      tags: isComingSoon ? this.calculateTags(quiz.examExercisesConfig?.start_date) : [],
    };
  };

  calculateScore(correctAnswers: number, totalQuestions: number): number {
    return Math.round((correctAnswers / totalQuestions) * 10 * 10) / 10;
  }

  calculateTags(startDate?: Date): string[] {
    if (!startDate) return [];
    const formattedDate = new Date(startDate).toLocaleDateString('pt-BR');
    return [formattedDate];
  }

  isFinishedQuiz = (quiz: QuizProps): boolean => quiz.studentFinished;

  isComingSoonQuiz = (quiz: QuizProps): boolean => {
    const startDate = quiz.examExercisesConfig?.start_date;
    return startDate ? new Date(startDate) > new Date() : false;
  };

  @computed
  get application() {
    return this.applicationModel.application;
  }

  @computed
  get user() {
    return this.userModel.user;
  }

  @computed
  get finishedQuizzes() {
    return this.quizzes.filter((quiz) => this.isFinishedQuiz(quiz) && !this.isComingSoonQuiz(quiz));
  }

  @computed
  get toDoQuizzes() {
    return this.quizzes.filter(
      (quiz) => !this.isFinishedQuiz(quiz) && !this.isComingSoonQuiz(quiz),
    );
  }

  @computed
  get comingSoonQuizzes() {
    return this.quizzes.filter(this.isComingSoonQuiz);
  }
}
